/** Vendors */
import dayjs from "dayjs";

/** Enums */
import {
  Auth0Role,
  EntityType,
  FileTag,
  RentalType,
  ResourceId,
  SystemName,
  TestimonialCategory,
  TransactionType,
} from "types";

/** Types */
import { IMember, IStringObject, ITransaction, IUnit } from "types";

interface ISortLists {
  baseline: string[];
  data: Array<IStringObject & { label: string; value: string }>;
}

const _dateFields = [
  "created",
  "date_purchased",
  "installed",
  "insurance_end",
  "insurance_start",
  "lease_end",
  "lease_start",
  "timestamp",
  "updated",
];

export const auth0Roles: IStringObject[] = [
  { label: "Contact", value: Auth0Role.Contact },
  { label: "Assistant", value: Auth0Role.Assistant },
  { label: "Investor", value: Auth0Role.Investor },
  { label: "Investment Bank", value: Auth0Role.InvestmentBank },
  { label: "Tax Professional", value: Auth0Role.Tax },
];

export const capexEnumToLabel: IStringObject = {
  appliances: "Appliances",
  fridge: "Fridge",
  hvac: "HVAC",
  roof: "Roof",
  solar: "Solar",
  stove: "Stove",
  washer: "Washing Machine",
  water_heater: "Water Heater",
  windows: "Windows",
};

export const entityTypes = [
  { label: "Business Trust", value: EntityType.Trust },
  { label: "Corporation (C or S Corp)", value: EntityType.Corporation },
  { label: "Cooperative (Co-op)", value: EntityType.COOP },
  { label: "General Partnership (GP)", value: EntityType.GeneralPartnership },
  { label: "Joint Venture", value: EntityType.JoinVenture },
  { label: "Limited Liability Company (LLC)", value: EntityType.LLC },
  {
    label: "Limited Liability Partnership (LLP)",
    value: EntityType.LLP,
  },
  { label: "Limited Partnership (LP)", value: EntityType.LP },
  { label: "Real Estate Investment Trust (REIT)", value: EntityType.REIT },
  { label: "Sole Proprietorship", value: EntityType.SoleProprietor },
];

export const propertyTypes: IStringObject[] = [
  { label: "SFH", value: "SFH" },
  { label: "Multifamily", value: "MultiFamily" },
  { label: "Condo", value: "Condo" },
  { label: "Town Home", value: "TownHome" },
  { label: "BRRR", value: "BRRR" },
  { label: "Other", value: "Other" },
];

export const relationshipTypes: IStringObject[] = [
  { label: "All", value: "all" },
  { label: "Closing Agent", value: "closing" },
  { label: "Director of Operations", value: "operations_director" },
  { label: "Financing", value: "financing" },
  { label: "Founder", value: "founder" },
  { label: "Leasing Manager", value: "leasing_manager" },
  { label: "Loan Originator", value: "loan_originator" },
  { label: "Operations Manager", value: "operations_manager" },
  { label: "Owner", value: "owner" },
  { label: "Property Management", value: "property_management" },
  { label: "President", value: "president" },
  { label: "Property Mgmt Lead", value: "property_management_lead" },
  { label: "Seller", value: "seller" },
  { label: "Senior Loan Originator", value: "senior_loan_originator" },
  { label: "Title Agent", value: "title" },
];

export const rentalTypes = [
  { label: "Long Term Rental", value: RentalType.LongTermRentals },
  { label: "Short Term Rental", value: RentalType.ShortTermRentals },
  { label: "Syndication", value: RentalType.Syndication },
  { label: "Other", value: RentalType.Other },
];

export const systemTypes = [
  { label: "Appliances", value: SystemName.Appliances },
  { label: "Fridge", value: SystemName.Fridge },
  { label: "HVAC", value: SystemName.HVAC },
  { label: "Roof", value: SystemName.Roof },
  { label: "Solar", value: SystemName.Solar },
  { label: "Stove", value: SystemName.Stove },
  { label: "Washing Machine", value: SystemName.WashingMachine },
  { label: "Water Heater", value: SystemName.WaterHeater },
  { label: "Windows", value: SystemName.Windows },
];
export const transactionTypes = [
  {
    column_enabled: true,
    extra: "Revenue from fees or miscellaneous income not categorized elsewhere",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Fees & Other Revenue",
    order: 1,
    value: "fees_and_other_revenue"
  },
  {
    column_enabled: true,
    extra: "Fees charged to tenants during the rental application process",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Application Fees",
    order: 2,
    value: "application_fees"
  },
  {
    column_enabled: false,
    extra: "Fees collected for cleaning services provided to tenants",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Cleaning Fee",
    order: 3,
    value: "cleaning"
  },
  {
    column_enabled: false,
    extra: "Charges imposed on tenants for late rent payments",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Late Fees",
    order: 4,
    value: "late_fee"
  },
  {
    column_enabled: false,
    extra: "Income from providing laundry facilities or services",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Laundry Fee",
    order: 5,
    value: "laundry_fee"
  },
  {
    column_enabled: false,
    extra: "Miscellaneous tenant charges not otherwise specified",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Other Fees",
    order: 6,
    value: "other_fees"
  },
  {
    column_enabled: false,
    extra: "Charges for tenant use of parking spaces or facilities",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Parking Fee",
    order: 7,
    value: "parking"
  },
  {
    column_enabled: false,
    extra: "Delivery fees incurred in the course of owning a rental property",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Delivery Fee",
    order: 8,
    value: "delivery_fee"
  },
  {
    column_enabled: false,
    extra: "Typically a non-refundable deposit made by your tenants to have a pet stay within your rental property",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Pet Fees",
    order: 9,
    value: "pet_deposit"
  },
  {
    column_enabled: false,
    extra: "Charges for tenant use of storage areas or units",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Storage Fee",
    order: 10,
    value: "storage_fee"
  },
  {
    column_enabled: false,
    extra: "Reimbursements or passthrough costs billed to tenants",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Tenant Pass-Throughs",
    order: 11,
    value: "tenant_pass_throughs"
  },
  {
    column_enabled: false,
    extra: "Income earned during the operation of your rental property",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Royalties Received",
    order: 12,
    value: "royalties"
  },
  {
    column_enabled: false,
    extra: "Earned income during the operation of your rental property",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Income",
    order: 13,
    value: "income"
  },
  {
    column_enabled: false,
    extra: "The rent collected from your tenants",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Rent",
    order: 14,
    value: "rent"
  },
  {
    column_enabled: false,
    extra: "Ongoing rental income from long-term lease agreements",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Long Term Rents",
    order: 15,
    value: "long_term_rents"
  },
  {
    column_enabled: false,
    extra: "Rental income from medium-length stays, typically 1–6 months",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Medium Term Rents",
    order: 16,
    value: "medium_term_rents"
  },
  {
    column_enabled: false,
    extra: "Rental payments received through the Section 8 housing program",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Section 8 Rents",
    order: 17,
    value: "section_8_rents"
  },
  {
    column_enabled: false,
    extra: "Revenue from short-term stays, such as vacation or Airbnb rentals",
    group: "revenue",
    group_label: "Revenue",
    is_loss: false,
    label: "Short Term Rents",
    order: 18,
    value: "short_term_rents"
  },
  {
    column_enabled: false,
    extra: "Day-to-day general expenses not categorized elsewhere",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "General Expenses",
    order: 19,
    value: "general_expenses"
  },
  {
    column_enabled: false,
    extra: "Costs related to promoting your rental properties",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Advertising",
    order: 20,
    value: "advertising"
  },
  {
    column_enabled: false,
    extra: "Vehicle and travel-related costs for property management",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Auto & Travel",
    order: 21,
    value: "auto_travel"
  },
  {
    column_enabled: false,
    extra: "Costs for tenant or business-related entertainment",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Entertainment",
    order: 22,
    value: "entertainment"
  },
  {
    column_enabled: false,
    extra: "Meals and food expenses related to property business",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Meals & Food",
    order: 23,
    value: "meal"
  },
  {
    column_enabled: false,
    extra: "Record costs related to visiting and managing properties, such as mileage, parking, or airfare.",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Travel",
    order: 24,
    value: "travel"
  },
  {
    column_enabled: false,
    extra: "Record costs related to visiting and managing properties, such as mileage, parking, or airfare.",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Vehicle Expenses",
    order: 25,
    value: "vehicle"
  },
  // {
  //   column_enabled: false,
  //   extra: "Fees paid to agents or brokers for leasing units",
  //   group: "operating_expenses",
  //   group_label: "Operating Expenses",
  //   is_loss: true,
  //   label: "Commissions",
  //   order: 26,
  //   value: "commissions"
  // },
  {
    column_enabled: false,
    extra: "Non-cash expense reflecting asset depreciation or resource depletion",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Depreciation Expenses or Depletion",
    order: 27,
    value: "depreciation"
  },
  // {
  //   column_enabled: false,
  //   extra: "Premiums paid to insure the rental property",
  //   group: "operating_expenses",
  //   group_label: "Operating Expenses",
  //   is_loss: true,
  //   label: "Insurance",
  //   order: 28,
  //   value: "insurance"
  // },
  // {
  //   column_enabled: false,
  //   extra: "Record expenses for legal and professional services, such as attorney fees, accounting services, or consultant fees.",
  //   group: "operating_expenses",
  //   group_label: "Operating Expenses",
  //   is_loss: true,
  //   label: "Legal & Other Professional Fees",
  //   order: 29,
  //   value: "legal_professional_fee"
  // },
  // {
  //   column_enabled: false,
  //   extra: "Fees paid to property managers or firms for oversight",
  //   group: "operating_expenses",
  //   group_label: "Operating Expenses",
  //   is_loss: true,
  //   label: "Management Fees",
  //   order: 30,
  //   value: "management_fees"
  // },
  // {
  //   column_enabled: false,
  //   extra: "Miscellaneous operating expenses not listed elsewhere",
  //   group: "operating_expenses",
  //   group_label: "Operating Expenses",
  //   is_loss: true,
  //   label: "Other Operating Expenses",
  //   order: 31,
  //   value: "other_operating_expenses"
  // },
  {
    column_enabled: true,
    definition: "maintenance_repair",
    extra:
      "The sum of maintenance and repair activities.",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Maintenance and Repairs",
    order: 32,
    value: "maintenance_repair",
  },
  // {
  //   column_enabled: false,
  //   extra: "Costs to repair and restore property or appliances",
  //   group: "operating_expenses",
  //   group_label: "Operating Expenses",
  //   is_loss: true,
  //   label: "Repairs",
  //   order: 29,
  //   value: "repair"
  // },
  {
    column_enabled: false,
    extra: "Record costs related to visiting and managing properties, such as mileage, parking, or airfare",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Lodging",
    order: 36,
    value: "hotel"
  },
  {
    column_enabled: false,
    extra:
      "Purchases made in support of the daily operation of your rental property",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Purchase",
    order: 37,
    value: "purchase",
  },
  {
    column_enabled: false,
    extra:
      "Wages paid to contractors or employees to maintain or otherwise support your rental property",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Wages",
    order: 38,
    value: "wage",
  },
  // {
  //   column_enabled: false,
  //   extra: "Costs related to general cleaning and property upkeep",
  //   group: "operating_expenses",
  //   group_label: "Operating Expenses",
  //   is_loss: true,
  //   label: "Cleaning & Maintenance",
  //   order: 33,
  //   value: "maintenance"
  // },
  {
    column_enabled: false,
    extra: "Labor expenses for cleaning and maintenance tasks",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "C & M Labor",
    order: 39,
    value: "cm_labor"
  },
  {
    column_enabled: false,
    extra: "Supplies and materials used for cleaning and maintenance",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "C & M Supplies",
    order: 40,
    value: "cm_supplies"
  },
  {
    column_enabled: false,
    extra: "Expenses for professional or routine janitorial services",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Cleaning & Janitorial",
    order: 41,
    value: "cleaning_janitorial"
  },
  {
    column_enabled: false,
    extra: "Costs related to maintaining shared or common property areas",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Common Area",
    order: 42,
    value: "common_area"
  },
  {
    column_enabled: false,
    extra: "Gardening, landscaping, and grounds maintenance expenses",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Gardening & Landscaping",
    order: 43,
    value: "gardening_landscaping"
  },
  {
    column_enabled: false,
    extra: "Consumables like linens, soaps, and other hospitality supplies",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Linens, Soaps, & Other Consumables",
    order: 44,
    value: "linens_soaps"
  },
  {
    column_enabled: false,
    extra: "Costs for painting interior or exterior property surfaces",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Painting",
    order: 45,
    value: "painting"
  },
  {
    column_enabled: false,
    extra: "Fees for government-required permits and property inspections",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Permits & Inspections",
    order: 46,
    value: "permits_inspections"
  },
  {
    column_enabled: false,
    extra: "Costs for pest control and extermination services",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Pest",
    order: 47,
    value: "pest"
  },
  {
    column_enabled: false,
    extra: "Maintenance and cleaning expenses for pools and spas",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Pool & Spa",
    order: 48,
    value: "pool_spa"
  },
  {
    column_enabled: false,
    extra: "Expenses for security, lock replacements, and key management",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Security, Locks & Keys",
    order: 49,
    value: "security_locks_keys"
  },
  {
    column_enabled: false,
    extra: "Costs related to removing snow from property areas",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Snow Removal",
    order: 50,
    value: "snow_removal"
  },
  {
    column_enabled: false,
    extra: "Fees paid to agents or platforms for securing tenants",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Commissions",
    order: 51,
    value: "commission"
  },
  {
    column_enabled: false,
    extra: "Payments to agents for tenant placement and lease signing",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Leasing Commissions",
    order: 52,
    value: "leasing_commissions"
  },
  {
    column_enabled: false,
    extra: "Other types of commission payments related to the property",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Other Commissions",
    order: 53,
    value: "other_commissions"
  },
  {
    column_enabled: false,
    extra: "General insurance costs for the property or business",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Insurance",
    order: 54,
    value: "insurance"
  },
  {
    column_enabled: false,
    extra: "Coverage for damages caused by earthquakes",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Earthquake",
    order: 55,
    value: "earthquake_insurance"
  },
  {
    column_enabled: false,
    extra: "Coverage for water damage caused by flooding",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Flood",
    order: 56,
    value: "flood_insurance"
  },
  {
    column_enabled: false,
    extra:
      "Protection for a homeowner and/or business owner against physical damages caused by unexpected and sudden events such as fires, severe storms, and other events",
    group: "insurance",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Hazard",
    order: 57,
    value: "hazard_insurance",
  },
  {
    column_enabled: false,
    extra: "Protection for loss or damage caused by windstorm during a hurricane",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Hurricane",
    order: 58,
    value: "hurricane_insurance"
  },
  {
    column_enabled: false,
    extra:
      "Protects you legally and financially from damages or injuries related to a rental property you own",
    group: "insurance",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Landlord Insurance",
    order: 59,
    value: "landlord_insurance",
  },
  {
    column_enabled: false,
    extra: "Insurance to protect against liability claims or lawsuits",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Liability",
    order: 60,
    value: "liability"
  },
  {
    column_enabled: false,
    extra: "Insurance for rental condominium units",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Rental Condo",
    order: 61,
    value: "rental_condo"
  },
  {
    column_enabled: false,
    extra: "Insurance for single-family or multi-family rental homes",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Rental Dwelling",
    order: 62,
    value: "rental_dwelling"
  },
  {
    column_enabled: false,
    extra: "Extended liability insurance beyond standard policies",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Umbrella",
    order: 63,
    value: "umbrella"
  },
  {
    column_enabled: false,
    extra: "Fees for legal, accounting, and other professional services",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Legal & Other Professional Fees",
    order: 64,
    value: "legal_professional_fee"
  },
  {
    column_enabled: false,
    extra: "Accounting services and tax preparation fees",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Accounting & Tax Fees",
    order: 65,
    value: "accounting_tax_fees"
  },
  {
    column_enabled: false,
    extra: "Professional fees for property valuation",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Appraisal Fees",
    order: 66,
    value: "appraisal_fees"
  },
  {
    column_enabled: false,
    extra: "Consulting services related to property operations or strategy",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Consulting Fees",
    order: 67,
    value: "consulting_fees"
  },
  {
    column_enabled: false,
    extra: "Fees associated with legal court proceedings",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Court Fees",
    order: 68,
    value: "court_fees"
  },
  {
    column_enabled: false,
    extra: "Legal or administrative fees incurred during evictions",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Eviction Fees",
    order: 69,
    value: "eviction_fees"
  },
  {
    column_enabled: false,
    extra: "Fees for property inspections and compliance checks",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Inspection Fees",
    order: 70,
    value: "inspection_fees"
  },
  {
    column_enabled: false,
    extra: "General legal services and attorney costs",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Legal Fees",
    order: 71,
    value: "legal_fees"
  },
  {
    column_enabled: false,
    extra: "Fees for surveying land or property boundaries",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Survey Fees",
    order: 72,
    value: "survey_fees"
  },
  {
    column_enabled: false,
    extra: "Fees paid to booking platforms or management services",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Management Fees",
    order: 72,
    value: "management_fees"
  },
  {
    column_enabled: false,
    extra: "Charges from online booking or rental platforms",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Booking & Platform Fees",
    order: 74,
    value: "booking_platform_fees"
  },
  {
    column_enabled: false,
    extra: "Fees for managing investment funds or capital",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Fund Management",
    order: 75,
    value: "fund_management"
  },
  {
    column_enabled: false,
    extra: "Fees paid to property managers for overseeing operations",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Property Management",
    order: 76,
    value: "property_management"
  },
  {
    column_enabled: false,
    extra: "Expenses related to collecting tenant rent payments",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Rent Collection",
    order: 77,
    value: "rent_collection"
  },
  {
    column_enabled: false,
    extra: "Service-related expenses for maintenance or tenant requests",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Service Calls",
    order: 78,
    value: "service_calls"
  },
  {
    column_enabled: false,
    extra: "Miscellaneous operating expenses not classified elsewhere",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Other Operating Expenses",
    order: 79,
    value: "other_operating_expenses"
  },
  {
    column_enabled: false,
    extra: "Costs for tenant background and credit checks",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Background & Credit Checks",
    order: 80,
    value: "background_credit_checks"
  },
  {
    column_enabled: false,
    extra: "Fees charged by banks for various financial services",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Bank Fees",
    order: 81,
    value: "bank_fees"
  },
  {
    column_enabled: false,
    extra: "Training or educational expenses for staff or owners",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Education",
    order: 82,
    value: "education"
  },
  {
    column_enabled: false,
    extra: "Gifts provided to tenants, staff, or business partners",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Gifts",
    order: 83,
    value: "gifts"
  },
  {
    column_enabled: false,
    extra: "Housing Authority (HOA) monthly and/or annual payments made",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "HOA Dues",
    order: 84,
    value: "hoa"
  },
  {
    column_enabled: false,
    extra: "Reimbursements from insurance reducing reported expenses",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Insurance Proceeds (Contra Expense)",
    order: 85,
    value: "insurance_proceeds"
  },
  {
    column_enabled: false,
    extra: "Costs for required business or operational licenses",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Licenses",
    order: 86,
    value: "licenses"
  },
  {
    column_enabled: false,
    extra: "Dues paid for business memberships or associations",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Memberships",
    order: 87,
    value: "memberships"
  },
  {
    column_enabled: false,
    extra: "Discounts or incentives given to tenants on rent",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Rent Concessions",
    order: 88,
    value: "rent_concessions"
  },
  {
    column_enabled: false,
    extra: "Subscription fees for software used in operations",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Software Subscriptions",
    order: 89,
    value: "software_subscriptions"
  },
  {
    column_enabled: false,
    extra:
      "Finance charges that may have been added to usage of your credit or loans",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Finance Charge",
    order: 90,
    value: "finance_charge",
  },
  {
    column_enabled: false,
    extra:
      "Tax payment made to your respective state for the operation of your business within that state. Typically on an annual basis.",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Franchise Tax Payment",
    order: 91,
    value: "ftb_payment",
  },
  // {
  //     column_enabled: false,
  //     extra: "Necessary actions taken to fix, restore, or replace damaged, broken, or worn-out components of a property, ensuring its structural integrity, functionality, and overall appearance are maintained or improved.",
  //     group: "operating_expenses",
  //     group_label: "Operating Expenses",
  //     is_loss: true,
  //     label: "Repairs",
  //     order: 83,
  //     value: "repair"
  // },
  {
    column_enabled: false,
    extra: "Repairs or replacements of appliances",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Appliance Repairs",
    order: 92,
    value: "appliance_repairs"
  },
  {
    column_enabled: false,
    extra: "Repairs to doors, windows, or related fixtures",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Door & Window Repairs",
    order: 93,
    value: "door_window_repairs"
  },
  {
    column_enabled: false,
    extra: "Expenses for repairing electrical systems or fixtures",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Electrical Repairs",
    order: 94,
    value: "electrical_repairs"
  },
  {
    column_enabled: false,
    extra: "Maintenance and repairs of HVAC systems",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "HVAC Repairs",
    order: 95,
    value: "hvac_repairs"
  },
  {
    column_enabled: false,
    extra: "Miscellaneous repair expenses not categorized",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Other Repairs",
    order: 96,
    value: "other_repairs"
  },
  {
    column_enabled: false,
    extra: "Repair services or labor costs",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Repairs Labor",
    order: 97,
    value: "repairs_labor"
  },
  {
    column_enabled: false,
    extra: "Supplies purchased for repairs or maintenance",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Repairs Supplies",
    order: 98,
    value: "repairs_supplies"
  },
  {
    column_enabled: false,
    extra: "Repair costs related to the roof or roofing systems",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Roof Repairs",
    order: 99,
    value: "roof_repairs"
  },
  {
    column_enabled: false,
    extra: "Purchase of items necessary for property operation",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Supplies",
    order: 33,
    value: "supplies"
  },
  {
    column_enabled: false,
    extra: "Track any office-related expenses, including supplies, software subscriptions, and postage.",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Office Supplies & Postage",
    order: 100,
    value: "office_supplies"
  },
  {
    column_enabled: false,
    extra: "General supply expenses not otherwise specified",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Other Supplies",
    order: 101,
    value: "other_supplies"
  },
  {
    column_enabled: false,
    extra: "Property-related taxes including real estate taxes",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Taxes",
    order: 34,
    value: "taxes"
  },
  {
    column_enabled: false,
    extra: "Taxes paid to city, state, or local authorities",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "City, State, & Local Taxes",
    order: 102,
    value: "city_state_local_taxes"
  },
  {
    column_enabled: false,
    extra: "Federal tax expenses related to the property or business",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Federal Taxes",
    order: 103,
    value: "federal_taxes"
  },
  {
    column_enabled: false,
    extra: "Annual or periodic property tax payments",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Property Taxes",
    order: 104,
    value: "property_tax"
  },
  {
    column_enabled: false,
    extra: "Taxes charged for short-term rental occupancy",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Short Term Occupancy Taxes",
    order: 105,
    value: "short_term_occupancy_taxes"
  },
  {
    column_enabled: false,
    extra: "Special tax assessments levied on the property",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Special Assessments",
    order: 106,
    value: "special_assessments"
  },
  {
    column_enabled: false,
    extra: "Costs for registering or renewing business licenses or tax IDs",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Tax Licenses & Registrations",
    order: 107,
    value: "tax_licenses_registrations"
  },
  {
    column_enabled: false,
    extra: "Monitor utility expenses such as water, sewer, gas, and electricity if the landlord is responsible for them.",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Utilities",
    order: 35,
    value: "utilities"
  },
  {
    column_enabled: false,
    extra: "Electric utility bills for the property",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Electric",
    order: 108,
    value: "electric"
  },
  {
    column_enabled: false,
    extra: "Costs for trash removal and recycling services",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Garbage & Recycling",
    order: 109,
    value: "garbage_recycling"
  },
  {
    column_enabled: false,
    extra: "Utility expenses for gas usage",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Gas",
    order: 110,
    value: "gas"
  },
  {
    column_enabled: false,
    extra: "Combined utility expenses for both gas and electricity",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Gas & Electric",
    order: 111,
    value: "gas_electric"
  },
  {
    column_enabled: false,
    extra: "Utility paymnents made for gas, electric, water, etc",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Cable & Internet",
    order: 112,
    value: "internet"
  },
  {
    column_enabled: false,
    extra: "Telephone fees incurred during the operation of your rental property",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Telephone",
    order: 113,
    value: "telephone"
  },
  {
    column_enabled: false,
    extra: "Water and sewage utility expenses",
    group: "operating_expenses",
    group_label: "Operating Expenses",
    is_loss: true,
    label: "Water & Sewer",
    order: 114,
    value: "water_sewer"
  },
  {
    column_enabled: false,
    extra: "Expenses for significant property improvements or upgrades",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Capital Expenditures",
    order: 115,
    value: "capital_improvement"
  },
  {
    column_enabled: false,
    extra: "Costs of purchasing or replacing appliances",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Appliances",
    order: 116,
    value: "appliances"
  },
  {
    column_enabled: false,
    extra: "Closing costs related to property transactions",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Closing Costs",
    order: 117,
    value: "closing_costs"
  },
  {
    column_enabled: false,
    extra: "Expenditures for doors and windows replacement or upgrades",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Doors & Windows",
    order: 118,
    value: "doors_windows"
  },
  {
    column_enabled: false,
    extra: "Spending on flooring installation or carpet replacement",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Flooring & Carpet",
    order: 119,
    value: "flooring_carpet"
  },
  {
    column_enabled: false,
    extra: "Investments in heating, ventilation, and air conditioning systems",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "HVAC",
    order: 120,
    value: "hvac"
  },
  {
    column_enabled: false,
    extra: "Costs for lawn care, outdoor improvements, and gardening",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Landscaping",
    order: 121,
    value: "landscaping"
  },
  {
    column_enabled: false,
    extra: "Expenditures for purchasing new property or assets",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "New Acquisitions",
    order: 122,
    value: "new_acquisitions"
  },
  {
    column_enabled: false,
    extra: "Spending on new furniture and equipment purchases",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "New Furniture & Equipment",
    order: 123,
    value: "new_furniture_equipment"
  },
  {
    column_enabled: false,
    extra: "Painting costs for property maintenance or improvement",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Painting",
    order: 124,
    value: "painting"
  },
  {
    column_enabled: false,
    extra: "Electrical or plumbing system improvements and repairs",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Plumbing & Electrical",
    order: 125,
    value: "plumbing_electrical"
  },
  {
    column_enabled: false,
    extra: "Remodeling costs for renovations or layout changes",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Remodeling",
    order: 126,
    value: "remodeling"
  },
  {
    column_enabled: false,
    extra: "Expenses for roof replacement or repairs",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Roof",
    order: 127,
    value: "roof"
  },
  {
    column_enabled: false,
    extra: "Payments made towards loans for property financing",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Loan Payments",
    order: 128,
    value: "loan_payments"
  },
  {
    column_enabled: false,
    extra: "Interest portion of your mortgage payments",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Mortgage Interest Payments",
    order: 129,
    value: "mortgage_interest_payments"
  },
  {
    column_enabled: false,
    extra: "The loan amount you are using to finance the purchase of this property",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Mortgage Payments",
    order: 130,
    value: "mortgage"
  },
  {
    column_enabled: false,
    extra: "Repayment of your financed loan for the property",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Heloc Payment",
    order: 131,
    value: "heloc"
  },
  {
    column_enabled: false,
    extra: "The principal portion of your mortgage that has been paid back. Requires 'Advanced Mortgage' inputs.",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Mortgage Principal Payments",
    order: 132,
    value: "principal"
  },
  {
    column_enabled: false,
    extra: "Repayment of your financed loan for the property",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Other Loan Interest Payments",
    order: 133,
    value: "loan_interest"
  },
  {
    column_enabled: false,
    extra: "Total payments made on other types of loans",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Other Loan Payments",
    order: 134,
    value: "other_loan_payments"
  },
  {
    column_enabled: false,
    extra: "Principal payments made on other types of loans",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Other Loan Principal Payments",
    order: 135,
    value: "other_loan_principal_payments"
  },
  {
    column_enabled: false,
    extra: "Capital payments related to acquiring or selling property",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Property Transactions",
    order: 136,
    value: "property_transactions"
  },
  {
    column_enabled: false,
    extra: "Initial payments made when purchasing a property",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Down Payments",
    order: 137,
    value: "down_payments"
  },
  {
    column_enabled: false,
    extra: "Funds received from mortgage lenders at closing",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Mortgage Disbursements Received",
    order: 138,
    value: "mortgage_disbursements_received"
  },
  {
    column_enabled: false,
    extra: "Funds received from non-mortgage loan disbursements",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Other Loan Disbursements Received",
    order: 139,
    value: "other_loan_disbursements_received"
  },
  {
    column_enabled: false,
    extra: "Money received from selling a property",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Sale Proceeds",
    order: 140,
    value: "sale"
  },
  {
    column_enabled: false,
    extra: "Payments made by credit card related to property expenses",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Transfers & Other",
    order: 141,
    value: "Transfers & Other"
  },
  {
    column_enabled: false,
    extra: "Payments made by credit card related to property expenses",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Credit Card Payments",
    order: 142,
    value: "credit_card_payments"
  },
  {
    column_enabled: false,
    extra: "Refunds for purchases or other transactions for your rental property",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Refund",
    order: 143,
    value: "refund"
  },
  {
    column_enabled: false,
    extra: "Payments held in escrow for future disbursement",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Escrow Payments",
    order: 144,
    value: "escrow_payments"
  },
  {
    column_enabled: false,
    extra: "Payments made into a general escrow account",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "General Escrow Payments",
    order: 145,
    value: "general_escrow_payments"
  },
  {
    column_enabled: false,
    extra: "Funds set aside for HOA dues through escrow",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "HOA Escrows",
    order: 146,
    value: "hoa_escrows"
  },
  {
    column_enabled: false,
    extra: "Escrow payments for insurance coverage",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Insurance Escrows",
    order: 146,
    value: "insurance_escrows"
  },
  {
    column_enabled: false,
    extra: "Payments held in escrow for PMI (Private Mortgage Insurance)",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "PMI Escrows",
    order: 147,
    value: "pmi_escrows"
  },
  {
    column_enabled: false,
    extra: "Allocate and record monthly property tax payments, or escrow amounts if applicable.",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Property Tax Escrows",
    order: 148,
    value: "property_tax_escrows"
  },
  {
    column_enabled: false,
    extra: "Interest income received from banking accounts",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Banking Interest",
    order: 149,
    value: "banking_interest"
  },
  {
    column_enabled: false,
    extra: "Interest charged to you for reasons other than your mortgage",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Other Interests",
    order: 150,
    value: "other_interest"
  },
  {
    column_enabled: false,
    extra: "Capital invested or withdrawn by property owners",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Owner Contributions/Distributions",
    order: 151,
    value: "owner_contributions_distributions"
  },
  {
    column_enabled: false,
    extra: "Interest earned on security deposits held in trust",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Security Deposit Interest Earned",
    order: 152,
    value: "security_deposit_interest_earned"
  },
  {
    column_enabled: false,
    extra: "Security Deposit obtained from your tenants. Can be either positive or negative.",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Security Deposits",
    order: 153,
    value: "security_deposit"
  },
  {
    column_enabled: false,
    extra: "Internal transfers between financial accounts",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Transfers Between Accounts",
    order: 154,
    value: "transfers_between_accounts"
  },
  {
    column_enabled: false,
    extra: "The total amount of money being transferred into and out of a business, especially as affecting liquidity",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Cash Flow",
    order: 155,
    value: "cashflow"
  },
  {
    column_enabled: false,
    extra: "Expenses not tied to a specific property",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: false,
    label: "Non-Property Expense",
    order: 156,
    value: "non_property_expense"
  },
  {
    column_enabled: false,
    extra: "Catch all for transactions that don't quite fit elsewhere",
    group: "loan_payments_CapEx",
    group_label: "Loan Payments & CapEx",
    is_loss: true,
    label: "Other",
    order: 157,
    value: "other",
  },
]

export const fileTags = [
  { key: "file-tage-0", label: "Title/Escrow", value: FileTag.Title },
  { key: "file-tage-1", label: "Property Management", value: FileTag.PM },
  { key: "file-tage-2", label: "Appraisal", value: FileTag.Appraisal },
  { key: "file-tage-3", label: "Insurance", value: FileTag.Insurance },
  { key: "file-tage-4", label: "Maintenance", value: FileTag.Maintenance },
  { key: "file-tage-5", label: "Taxes", value: FileTag.Taxes },
  { key: "file-tage-6", label: "Lease", value: FileTag.Lease },
  { key: "file-tage-7", label: "Mortgage", value: FileTag.Mortgage },
  { key: "file-tage-8", label: "Contract", value: FileTag.Contract },
  { key: "file-tage-9", label: "Other", value: FileTag.Other },
];

function _findComponent({
  address_components,
}: {
  address_components: { short_name: string; types: string[] }[];
}) {
  return (str: string) => {
    return address_components.find((p) => {
      return p.types.indexOf(str) > -1;
    });
  };
}

function _reduceAmount(r: number, transaction: ITransaction) {
  const amount = transaction.amount || 0;
  const is_loss = transaction.is_loss ? -1 : 1;
  r += amount * is_loss;
  return r;
}

function _sumByName(arr: ITransaction[], name = "") {
  return arr
    .filter((t) => t.name.toLowerCase().includes(name))
    .reduce(_reduceAmount, 0);
}

export const calculate = {
  cashflow: (arr: ITransaction[]) => arr.reduce(_reduceAmount, 0),
  // Forecast amount over a duration in months
  forecasted: {
    vacancy: (percent = 0, due: number) => {
      return Math.floor(((percent / 100) * due - due) * -1);
    },
  },
  maintenance: (arr: ITransaction[]) => _sumByName(arr, "maintenance"),
  mortgage: (arr: ITransaction[]) => _sumByName(arr, "mortgage"),
  pmfee: (arr: ITransaction[]) => _sumByName(arr, "property_management_fee"),
  rent: (arr: ITransaction[]) => _sumByName(arr, "rent"),
  rentExpected: (arr: ITransaction[]) => {
    return arr
      .filter((t) => t.name.toLowerCase().includes("rent"))
      .filter((t) => typeof t.amount_expected === "number")
      .reduce((r, t) => {
        if (t.amount_expected) {
          r += t.amount_expected;
        }
        return r;
      }, 0);
  },
  vacancyIsOverThreshold: ({
    transactions,
    units,
  }: {
    transactions: ITransaction[];
    units: IUnit[];
  }): boolean => {
    const expectedRent = calculate.rentExpected(transactions);
    const rent = calculate.rent(transactions);

    const vacancies = units.reduce(
      (r: { count: number; total: number }, u: IUnit) => {
        const value = u.estimated_annual_vacancy || 0;
        r.total += value;
        r.count += value ? 1 : 0;
        return r;
      },
      { count: 0, total: 0 }
    );
    const averageVacancy = vacancies.total / vacancies.count;
    return (1 + averageVacancy / 100) * rent < expectedRent;
  },
};

export function daysRemaining(dayInUnix: number): {
  color: string;
  days: number;
  percentage: number;
} {
  let color = "active";
  const days = dayjs().diff(dayInUnix || new Date().getTime(), "days") * -1;

  const today = new Date().getTime();
  const thirtyDays = 1000 * 60 * 60 * 24 * 30;
  if (!dayInUnix) color = "color-1";
  else if (dayInUnix < thirtyDays + today) color = "red";
  else if (dayInUnix < thirtyDays * 2 + today) color = "orange";
  else if (dayInUnix < thirtyDays * 3 + today) color = "yellow";

  return {
    color,
    days,
    percentage: (days / 365) * 100,
  };
}

export function determineLabel(key: string): string {
  let match = transactionTypes.find((a) => a.value === key);
  return !match ? toTitleCase(key) : toTitleCase(match.label.replace("_", " "));
}

/** Convert Record Unix Timestamps to DayJS Class */
export function formatDatesToDayjs(values: any) {
  const params = { ...values };

  for (const key in params) {
    /** Step 1. Loop over keys for a date field match */
    if (_dateFields.includes(key)) {
      params[key] = dayjs(params[key]).valueOf();
    }
    if (params[key] === "removed") {
      delete params[key];
    }
  }

  /** Property Specific Formatting */
  if (params?.resource_id === ResourceId.Property) {
    params.date_purchased = params.date_purchased
      ? dayjs(params.date_purchased)
      : dayjs();
    params.insurance_end = params.insurance_end
      ? dayjs(params.insurance_end)
      : dayjs().add(1, "year");
    params.insurance_start = params.insurance_start
      ? dayjs(params.insurance_start)
      : dayjs();
  }

  /** System Specific Formatting */
  if (params?.resource_id === ResourceId.System && params.installed) {
    params.installed = params.installed ? dayjs(params.installed) : dayjs();
  }

  /** Transaction Specific Formatting */
  if (params?.resource_id === ResourceId.Transaction) {
    params.timestamp = params.timestamp ? dayjs(params.timestamp) : dayjs();
  }

  /** Unit Specific Formatting */
  if (params?.resource_id === ResourceId.Unit) {
    params.lease_end = params.lease_end
      ? dayjs(params.lease_end)
      : dayjs().add(1, "year");
    params.lease_start = params.lease_start
      ? dayjs(params.lease_start)
      : dayjs();
  }

  return params;
}

/** Convert Record Unix Timestamps from DayJS Class */
export function formatDatesFromDayjs(values: any) {
  const params = { ...values };

  const normalizeFields = ["name"];

  for (const key in params) {
    /** Step 1. Loop over keys for a date field match */
    if (_dateFields.includes(key) && params[key]) {
      if (params.clear_date) {
        params[key] = 0;
      } else {
        if (key === 'timestamp') {
          console.log(params[key])
          const now = dayjs();
          const date = params[key];
          const dateWithCurrentTime = date
            .hour(now.hour())
            .minute(now.minute())
            .second(now.second())
            .millisecond(now.millisecond());
          params[key] = dayjs(dateWithCurrentTime).valueOf();
        } else {
          params[key] = dayjs(params[key]).valueOf();
        }
      }
    }

    /** Step 2. Normalize certain fields */
    if (normalizeFields.includes(key)) {
      params[key] = Array.isArray(params[key]) ? params[key]?.[0] : params[key];
    }

    /** Step 3. Remove location if its empty object (edge case) */
    if (key === "location") {
      if (!Object.keys(params[key]).length) {
        delete params[key];
      }
    }
  }

  return params;
}

export function formatFileName(str: string): string {
  return str.replace(/[^a-zA-Z0-9.]/g, "-").toLowerCase();
}

export function formatName(member: IMember): string {
  let parts = [];
  if (!member) return "";
  if (member.given_name) parts.push(member.given_name);
  if (member.family_name) parts.push(member.family_name);
  if (parts.length === 0 && member.email) {
    parts.push(member.email);
  }
  if (parts.length === 0 && member.business_email) {
    parts.push(member.business_email);
  }
  return parts.join(" ");
}

/**
 * @description Pass an Array of Objects. Hone in (1) key, extract,
 * @continued remove duplicates and return array of unique values
 */
export function getUniqueValues(arr = [], key = "resource_name") {
  const distilled = arr.reduce(
    (r, property) => ({
      ...r,
      [property[key]]: property[key],
    }),
    {}
  );

  return Object.values(distilled).filter((value) => value);
}

export function googlePlaceToObject({
  address_components,
  formatted_address,
  geometry,
  place_id,
}: any) {
  const component = _findComponent({ address_components });
  const city1 = component("locality")?.short_name;
  const city2 = component("administrative_area_level_2")?.short_name;
  const city3 = component("political")?.short_name;
  const county = component("administrative_area_level_1")?.short_name;
  const country = component("country")?.short_name;

  return {
    address: [
      component("street_number")?.short_name,
      component("route")?.short_name,
    ]
      .filter((str) => str)
      .join(" "),
    city: city1 || city2 || city3,
    county: city2 || county,
    country,
    formatted_address,
    location: {
      lat: geometry.location.lat(),
      lon: geometry.location.lng(),
    },
    place_id,
    state: component("administrative_area_level_1")?.short_name || "",
    zip_code: component("postal_code")?.short_name,
  };
}

export function keygen() {
  let d =
    new Date().getTime() + Math.floor(Math.random() * 1000000000000000000);
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    return (c == "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function removeDuplicates(arr = [], key = "resource_name") {
  return Object.values(
    (arr || []).reduce((r, props) => {
      const subkey = props[key];
      r[subkey] = props;

      return r;
    }, {})
  );
}

export const randomResourceName = (resource_id: ResourceId) => {
  return `${resource_id}-${keygen()}`;
};

export const sortLists = ({ baseline = [], data = [] }: ISortLists) => {
  return [...data].sort((a: IStringObject, b: IStringObject) => {
    let indexA = baseline.indexOf(a?.value);
    let indexB = baseline.indexOf(b?.value);
    if (indexA === -1) indexA = data.length + baseline.indexOf(a?.value);
    if (indexB === -1) indexB = data.length + baseline.indexOf(b?.value);

    return indexA - indexB;
  });
};

export const testimonialCategories = [
  {
    key: "testimonial-category-0",
    label: "Data Management",
    value: TestimonialCategory.DataManagement,
  },
  {
    key: "testimonial-category-1",
    label: "Documentation & Storage Management",
    value: TestimonialCategory.DocumentationStorage,
  },
  {
    key: "testimonial-category-2",
    label: "Operational Efficiency",
    value: TestimonialCategory.OperationalEfficiency,
  },
  {
    key: "testimonial-category-4",
    label: "Overall",
    value: TestimonialCategory.Overall,
  },
  {
    key: "testimonial-category-5",
    label: "Real Estate Scalability",
    value: TestimonialCategory.RealEstateScalability,
  },
  {
    key: "testimonial-category-6",
    label: "Reporting Analytics",
    value: TestimonialCategory.ReportingAnalytics,
  },
  {
    key: "testimonial-category-7",
    label: "Tax Reporting",
    value: TestimonialCategory.TaxReporting,
  },
  {
    key: "testimonial-category-3",
    label: "Other",
    value: TestimonialCategory.Other,
  },
];

export function toTitleCase(str = "") {
  return !str
    ? ""
    : str.replace(/[^a-zA-Z0-9]/g, " ").replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
}

export function transactionOptions(options: any) {
  return options.filter((option: any) => {
    return option.value !== "maintenance_repair";
  });
}